import type { Capability } from '@gsls/shared';
import { Link } from 'react-router-dom';
import { useSession } from '../lib/session';

/**
 * The thumb-reachable action bar shown on phones.
 *
 * On a wide screen the page header already holds these actions; on a phone the
 * same actions ride a bar pinned to the bottom of the content, within reach. It
 * is gated on the same capability the API requires, and it hides itself while
 * the screen's own primary panel is open so two "primary" actions never compete.
 */
export interface BarAction {
  label: string;
  to?: string;
  onClick?: () => void;
  capability?: Capability;
  tone?: 'primary' | 'outline';
}

const TONES = {
  primary: 'border-transparent bg-brand text-white hover:bg-[color:var(--gs-primary-hover)]',
  outline: 'border-surface-border bg-white text-brand-deep hover:border-brand',
} as const;

export function StickyActionBar({
  actions,
  hidden = false,
  label = 'Primary actions',
}: {
  actions: BarAction[];
  hidden?: boolean;
  label?: string;
}) {
  const { can } = useSession();
  const allowed = actions.filter((action) => !action.capability || can(action.capability));
  if (hidden || allowed.length === 0) return null;

  const className = (action: BarAction) =>
    `inline-flex h-11 items-center justify-center rounded-full border px-4 text-xs font-bold uppercase tracking-wide transition md:h-9 ${TONES[action.tone ?? 'outline']}`;

  return (
    <nav
      aria-label={label}
      className="sticky bottom-0 z-20 -mx-4 mt-6 border-t border-surface-border bg-white/95 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur print:hidden sm:-mx-6 md:hidden"
    >
      <div className="flex flex-wrap gap-2">
        {allowed.map((action) =>
          action.to ? (
            <Link key={action.label} to={action.to} className={className(action)}>
              {action.label}
            </Link>
          ) : (
            <button key={action.label} type="button" onClick={action.onClick} className={className(action)}>
              {action.label}
            </button>
          ),
        )}
      </div>
    </nav>
  );
}
